'use client';

import React, { useMemo, useState, useEffect, useRef, memo } from 'react';
import { Page, Section, MenuSection, MenuItem } from '@/types';
import MenuSectionRenderer from './MenuSectionRenderer';
import SectionRenderer from './SectionRenderer';
import DesktopSnapScroll from './DesktopSnapScroll';
import SectionWrapper from './SectionWrapper';
import VideoPreloader from './VideoPreloader';
import MenuSectionsRenderer from './MenuSectionsRenderer';
import PageContentWrapper from './PageContentWrapper';
import { FirstSectionProvider } from '@/contexts/FirstSectionContext';
import PageRendererWrapper from './PageRendererWrapper';
import { extractMediaUrlsFromSection } from '@/lib/section-media-utils';
import { usePreview } from '@/contexts/PreviewContext';
import { extractFallbackImagesFromSection } from '@/lib/video-fallback-utils';
import { mediaCache } from '@/lib/media-cache';
import { getMediaTypeFromUrl } from '@/lib/video-utils';
import { PublicFormData, PublicPolicyData } from '@/lib/public-site-data';


interface ClientPageRendererProps {
  page: Page;
  sections: Section[];
  menuSections: MenuSection[];
  menuItems?: MenuItem[];
  subscriptionForm?: PublicFormData | null;
  policies?: PublicPolicyData[];
}

function ClientPageRenderer({
  page,
  sections,
  menuSections,
  menuItems = [],
  subscriptionForm = null,
  policies = [],
}: ClientPageRendererProps) {
  const preview = usePreview();
  const isPreviewMode = preview?.isPreviewMode ?? false;
  
  // Start with true to not block initial render - images will preload in background
  const [fallbackImagesLoaded, setFallbackImagesLoaded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile - defer to reduce initial JS execution
  // Note: Most mobile detection can be done with CSS @media queries
  // This is only needed for JS-specific behavior
  useEffect(() => {
    // Defer mobile detection until after first paint
    const rafId = requestAnimationFrame(() => {
      const checkMobile = () => {
        // Use CSS media query when possible, fallback to JS for user agent detection
        const isMobileResult = window.innerWidth <= 768 ||
          /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setIsMobile(isMobileResult);
      };
      checkMobile();
      // Debounce resize handler to reduce layout thrash
      let resizeTimeout: NodeJS.Timeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(checkMobile, 150);
      };
      window.addEventListener('resize', handleResize, { passive: true });
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(resizeTimeout);
      };
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (!page || !page.isActive) {
    return null;
  }

  // Sections are already filtered and ordered by PageRenderer
  // Just use them directly - no need to filter again
  // Use ref to track previous values and only recompute if IDs actually changed
  const prevSectionsRef = useRef<Section[]>(sections);
  const prevPageSectionsResultRef = useRef<Section[]>(sections);
  
  const pageSections = useMemo(() => {
    // Check if sections actually changed by comparing IDs
    const currentSectionIds = sections.map(s => s.id).join(',');
    const prevSectionIds = prevSectionsRef.current.map(s => s.id).join(',');
    
    // Only recompute if IDs actually changed
    if (currentSectionIds === prevSectionIds) {
      return prevPageSectionsResultRef.current;
    }
    
    // Update refs
    prevSectionsRef.current = sections;
    prevPageSectionsResultRef.current = sections;
    
    return sections;
  }, [sections]);

  // Get menu sections for this page, sorted by order
  // page.menuSections can contain either IDs (from database) or names (from JSON like "header-1", "footer-1")
  // Only show menu sections that are explicitly defined in page.menuSections
  const pageMenuSections = useMemo(() => {
    let filtered: MenuSection[];
    
    if (!page.menuSections || page.menuSections.length === 0) {
      // If no menu sections specified for the page:
      // - In preview mode, show default header and footer (first of each type)
      // - In production, don't show any
      if (isPreviewMode) {
        const defaultHeader = menuSections.find(ms => ms.type === 'HEADER' && ms.isActive);
        const defaultFooter = menuSections.find(ms => ms.type === 'FOOTER' && ms.isActive);
        filtered = [defaultHeader, defaultFooter].filter((ms): ms is MenuSection => ms !== undefined);
      } else {
        filtered = [];
      }
    } else {
      // Filter by matching ID or name - only include what's explicitly listed
      filtered = menuSections.filter(ms => {
        // Match by exact ID (database or JSON id like "header-1")
        if (page.menuSections && page.menuSections.includes(ms.id)) return true;
        // Match by exact name (JSON compatibility like "header-1" matching name "Main Header" won't work, but "Main Header" matching "Main Header" will)
        if (page.menuSections && page.menuSections.includes(ms.name)) return true;
        // Match by pattern: if page.menuSections has "header-1" and menu section has id "header-1" or name contains the pattern
        // This handles cases where JSON uses "header-1" but we need to match by the actual menu section
        if (page.menuSections && page.menuSections.some(id => {
          const idLower = id.toLowerCase();
          const msIdLower = ms.id.toLowerCase();
          const msNameLower = ms.name.toLowerCase();
          // Exact match by id or name (already handled above, but check pattern matches)
          if (idLower === msIdLower || idLower === msNameLower) return true;
          // Pattern match: "header-1" should match menu section with id "header-1" or name that contains "header"
          if (idLower.includes('header') && ms.type === 'HEADER' && (msIdLower.includes('header') || msNameLower.includes('header'))) return true;
          if (idLower.includes('footer') && ms.type === 'FOOTER' && (msIdLower.includes('footer') || msNameLower.includes('footer'))) return true;
          return false;
        })) return true;
        return false;
      });
    }
    
    return filtered.sort((a, b) => a.order - b.order);
  }, [menuSections, page.menuSections]);

  // Separate header and footer
  const header = useMemo(() => pageMenuSections.find(ms => ms.type === 'HEADER'), [pageMenuSections]);
  const footer = useMemo(() => pageMenuSections.find(ms => ms.type === 'FOOTER'), [pageMenuSections]);

  // Enable snap scroll for home page (slug === ''), services page (slug === 'services'), and about page (slug === 'about')
  // Disable snap scroll when in preview mode to prevent blocking parent page scrolling
  // DISABLED: Snap scroll temporarily disabled
  const isHomePage = page.slug === '';
  const isServicesPage = page.slug === 'services';
  const isAboutPage = page.slug === 'about';
  const shouldUseSnapScroll = false; // Temporarily disabled

  // Get the first section ID to remove padding from it
  const firstSectionId = pageSections.length > 0 ? pageSections[0].id : null;

  // Extract fallback images from all sections (highest priority - load first)
  // Prioritize fallback images from sections with videos (they need frame fallbacks)
  // Extract for both mobile and desktop to avoid recomputation when isMobile changes
  // Use stable section IDs string as dependency instead of array reference
  const sectionIdsString = useMemo(() => 
    pageSections.map(s => s.id).join(','), 
    [pageSections]
  );
  const prevFallbackImageUrlsRef = useRef<string[]>([]);
  const prevSectionIdsStringRef = useRef<string>('');
  
  const fallbackImageUrls = useMemo(() => {
    // Check if section IDs actually changed
    if (sectionIdsString === prevSectionIdsStringRef.current) {
      return prevFallbackImageUrlsRef.current;
    }
    
    const videoSectionFallbacks: string[] = [];
    const otherSectionFallbacks: string[] = [];
    const seen = new Set<string>();
    
    // Extract for both mobile and desktop to get all possible fallback images
    // This prevents recomputation when isMobile changes
    pageSections.forEach(section => {
      const hasVideo = !!section.sectionData?.backgroundVideo;
      // Extract for both mobile and desktop to get all fallback images
      const fallbackImagesMobile = extractFallbackImagesFromSection(section, true);
      const fallbackImagesDesktop = extractFallbackImagesFromSection(section, false);
      // Combine and deduplicate
      const allFallbackImages = [...fallbackImagesMobile, ...fallbackImagesDesktop];
      
      allFallbackImages.forEach(img => {
        if (img.src && !seen.has(img.src)) {
          seen.add(img.src);
          // Prioritize fallbacks from sections with videos
          if (hasVideo) {
            videoSectionFallbacks.push(img.src);
          } else {
            otherSectionFallbacks.push(img.src);
          }
        }
      });
    });
    
    // Return video section fallbacks first, then others
    const result = [...videoSectionFallbacks, ...otherSectionFallbacks];
    
    // Update refs
    prevSectionIdsStringRef.current = sectionIdsString;
    prevFallbackImageUrlsRef.current = result;
    
    return result;
  }, [pageSections, sectionIdsString]); // Use stable sectionIdsString

  // Preload fallback images in background (non-blocking)
  // Rendering is not blocked - fallbackImagesLoaded starts as true
  useEffect(() => {
    if (fallbackImageUrls.length === 0) {
      // No fallback images to load
      return;
    }

    let cancelled = false;

    // Preload all fallback images with highest priority (non-blocking)
    // Rendering is already enabled - this just preloads in background
    const preloadFallbacks = async () => {
      try {
        // Filter to only images (not videos)
        // Include data URLs and blob URLs as they are images
        const imageUrls = fallbackImageUrls.filter(url => {
          // Data URLs and blob URLs are images, include them
          if (url.startsWith('data:') || url.startsWith('blob:')) {
            return true;
          }
          // Filter out actual videos (check for video extensions in the URL)
          const mediaType = getMediaTypeFromUrl(url);
          // If mediaType is null (unknown), treat as image (don't filter out)
          // Only filter out if explicitly identified as video
          return mediaType !== 'video';
        });

        if (imageUrls.length === 0) {
          return;
        }

        // Preload with highest priority (non-blocking)
        // Don't await - let it run in background
        mediaCache.preloadBatch(imageUrls, 'high').catch(() => {
          // Silently handle errors
        });
      } catch (error) {
        // On error, continue - already marked as loaded
      }
    };

    preloadFallbacks();

    return () => {
      cancelled = true;
    };
  }, [fallbackImageUrls]);

  // Extract media URLs from each section for preloading
  const sectionsWithMedia = useMemo(() => {
    return pageSections.map(section => ({
      section,
      mediaUrls: extractMediaUrlsFromSection(section),
    }));
  }, [pageSections]);

  // Separate sections by type while maintaining original order
  // Priority: sections with image backgrounds first, then sections with videos, then others
  const { sectionsWithImageBackgrounds, sectionsWithVideos, sectionsWithoutMedia } = useMemo(() => {
    const withImage: typeof sectionsWithMedia = [];
    const withVideo: typeof sectionsWithMedia = [];
    const withoutMedia: typeof sectionsWithMedia = [];

    sectionsWithMedia.forEach(({ section, mediaUrls }) => {
      const hasImage = section.sectionData?.backgroundImage;
      const hasVideo = section.sectionData?.backgroundVideo;
      
      if (hasImage && !hasVideo) {
        // Section has image background but no video - render first
        withImage.push({ section, mediaUrls });
      } else if (hasVideo) {
        // Section has video - render after images
        withVideo.push({ section, mediaUrls });
      } else {
        // Section has no media - render last
        withoutMedia.push({ section, mediaUrls });
      }
    });

    return { sectionsWithImageBackgrounds: withImage, sectionsWithVideos: withVideo, sectionsWithoutMedia: withoutMedia };
  }, [sectionsWithMedia]);

  // Render sections in priority order: image backgrounds first, then videos, then others
  // Maintains original section order within each category
  const renderSections = (sectionsToRender: typeof sectionsWithMedia, startIndex: number = 0) => {
    return sectionsToRender.map(({ section, mediaUrls }, idx) => {
      const index = startIndex + idx;
      return (
        <SectionWrapper key={section.id}>
          <SectionRenderer section={section} isFirstSection={index === 0} sectionIndex={index} mediaUrls={mediaUrls} />
        </SectionWrapper>
      );
    });
  };

  return (
    <PageRendererWrapper>
      <FirstSectionProvider firstSectionId={firstSectionId}>
        {/* VideoPreloader now only manages videosLoaded state - preloading handled globally */}
        <VideoPreloader sections={pageSections}>
          <PageContentWrapper>
            {/* Render sections in priority order: image backgrounds first, then videos, then others */}
            {shouldUseSnapScroll ? (
              <>
                <div className="block md:hidden">
                    <main className="flex-grow w-full overflow-x-hidden" style={{ paddingTop: 0, marginTop: 0 }}>
                      {/* Render sections in priority order */}
                      {fallbackImagesLoaded && (
                        <>
                          {/* 1. Sections with image backgrounds (render first) */}
                          {renderSections(sectionsWithImageBackgrounds, 0)}
                          {/* 2. Sections with videos (render after images) */}
                          {renderSections(sectionsWithVideos, sectionsWithImageBackgrounds.length)}
                          {/* 3. Other sections (render last) */}
                          {renderSections(sectionsWithoutMedia, sectionsWithImageBackgrounds.length + sectionsWithVideos.length)}
                        </>
                      )}
                      {/* Footer renders inside snap scroll - only if available in page's menuSections */}
                      {footer && fallbackImagesLoaded && (
                        <MenuSectionRenderer
                          menuSection={footer}
                          menuItems={menuItems}
                          subscriptionForm={subscriptionForm}
                          policies={policies}
                        />
                      )}
                    </main>
                </div>
                <div className="hidden md:block">
                  <DesktopSnapScroll maxSections={2}>
                    <main className="flex-grow w-full overflow-x-hidden" style={{ paddingTop: 0, marginTop: 0 }}>
                      {/* Render sections in priority order */}
                      {fallbackImagesLoaded && (
                        <>
                          {/* 1. Sections with image backgrounds (render first) */}
                          {renderSections(sectionsWithImageBackgrounds, 0)}
                          {/* 2. Sections with videos (render after images) */}
                          {renderSections(sectionsWithVideos, sectionsWithImageBackgrounds.length)}
                          {/* 3. Other sections (render last) */}
                          {renderSections(sectionsWithoutMedia, sectionsWithImageBackgrounds.length + sectionsWithVideos.length)}
                        </>
                      )}
                      {/* Footer renders inside snap scroll - only if available in page's menuSections */}
                      {footer && fallbackImagesLoaded && (
                        <MenuSectionRenderer
                          menuSection={footer}
                          menuItems={menuItems}
                          subscriptionForm={subscriptionForm}
                          policies={policies}
                        />
                      )}
                    </main>
                  </DesktopSnapScroll>
                </div>
              </>
            ) : (
              <main className="flex-grow w-full overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Render sections in priority order: image backgrounds first, then videos, then others */}
                {fallbackImagesLoaded && (
                  <>
                    {/* 1. Sections with image backgrounds (render first) */}
                    {renderSections(sectionsWithImageBackgrounds, 0)}
                    {/* 2. Sections with videos (render after images) */}
                    {renderSections(sectionsWithVideos, sectionsWithImageBackgrounds.length)}
                    {/* 3. Other sections (render last) */}
                    {renderSections(sectionsWithoutMedia, sectionsWithImageBackgrounds.length + sectionsWithVideos.length)}
                  </>
                )}
                {/* Footer renders after fallback images load - only if available in page's menuSections */}
                {footer && fallbackImagesLoaded && (
                  <MenuSectionsRenderer
                    footer={footer}
                    menuItems={menuItems}
                    subscriptionForm={subscriptionForm}
                    policies={policies}
                  />
                )}
              </main>
            )}
            
            {/* Header renders after fallback images load - no waiting for videos */}
            {/* IMPORTANT: render only header here to avoid duplicating footer. */}
            {header && fallbackImagesLoaded && (
              <MenuSectionRenderer
                menuSection={header}
                menuItems={menuItems}
                footerMenuSection={footer}
                subscriptionForm={subscriptionForm}
              />
            )}
          </PageContentWrapper>
        </VideoPreloader>
      </FirstSectionProvider>
    </PageRendererWrapper>
  );
}

// Memoize to prevent unnecessary re-renders when props haven't changed
export default memo(ClientPageRenderer, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  const propsEqual = 
    prevProps.page?.id === nextProps.page?.id &&
    prevProps.page?.slug === nextProps.page?.slug &&
    prevProps.page?.isActive === nextProps.page?.isActive &&
    prevProps.sections?.length === nextProps.sections?.length &&
    prevProps.menuSections?.length === nextProps.menuSections?.length &&
    // Check if section IDs are the same (more efficient than JSON.stringify)
    prevProps.sections?.every((s, i) => s?.id === nextProps.sections?.[i]?.id) &&
    prevProps.menuSections?.every((ms, i) => ms?.id === nextProps.menuSections?.[i]?.id);
  
  return propsEqual;
});
